import React, { useState } from 'react';
import StickerSelector, { STICKER_IMAGES } from '../shared/StickerSelector';

// Logo from public folder - accessed via absolute path
const dubaiPoliceLogo = '/transparent-logo-vertical.png';

const ResultScreen = ({ generatedImage, styleName, onRetake, onNext }) => {
  const [selectedSticker, setSelectedSticker] = useState(null);

  // Handle next with sticker info
  const handleNext = () => {
    onNext(selectedSticker);
  };

  return (
    <div className="h-screen relative overflow-hidden bg-gradient-to-b from-white via-[#F6FBF9] to-[#E4EFEA]">
      {/* Vertical spine */}
      <div className="forensic-spine" />

      {/* Background layers */}
      <div className="forensic-grid-overlay opacity-15" />
      <div className="forensic-map-overlay opacity-[0.04]" />
      <div className="forensic-circuit-lines" />

      {/* Green blocks */}
      <div className="forensic-block forensic-block-2 opacity-60" />
      <div className="forensic-corner-tl" />

      {/* Main content */}
      <div className="relative z-10 h-full flex flex-col px-8 py-6 gap-3">
        {/* Centered Header with Logo */}
        <header className="flex flex-col items-center">
          <img src={dubaiPoliceLogo} alt="Dubai Police" className="h-16 w-auto mb-2" />
          <h1 className="text-lg font-bold tracking-[0.1em] uppercase text-[#1A1A1A]">
            Your Result <span className="text-[#6B7770] font-arabic font-normal">| نتيجتك</span>
          </h1>
        </header>

        {/* Header line */}
        <div className="h-[2px] bg-gradient-to-r from-transparent via-[#0e734b] to-transparent" />

        {/* Image display with sticker preview */}
        <main className="flex-1 flex items-center justify-center min-h-0 py-2 overflow-hidden">
          <div className="relative h-full w-full flex items-center justify-center">
            <div className="forensic-card p-3 h-full max-w-full flex items-center justify-center overflow-hidden">
              <div className="relative h-full w-auto flex items-center justify-center">
                <img
                  src={generatedImage}
                  alt="Transformed result"
                  className="max-h-full w-auto object-contain rounded-sharp border-2 border-[#0e734b]/20"
                />
                {/* Sticker Preview Overlay - positioned within the image area (not on frame) */}
                {selectedSticker && (
                  <StickerOverlay sticker={selectedSticker} />
                )}
              </div>
            </div>
          </div>
        </main>

        {/* Sticker Selector */}
        <div className="px-4">
          <StickerSelector
            selectedSticker={selectedSticker}
            onSelectSticker={setSelectedSticker}
            onClearSticker={() => setSelectedSticker(null)}
          />
        </div>

        {/* Action buttons - Bilingual */}
        <footer className="flex justify-center gap-4 py-2">
          <button
            onClick={onRetake}
            className="forensic-btn-secondary px-10 py-3 flex items-center gap-2"
          >
            <span>←</span>
            <span className="font-title uppercase">Retake</span>
            <span className="opacity-60">|</span>
            <span className="font-arabic">إعادة</span>
          </button>
          <button
            onClick={handleNext}
            className="forensic-btn-primary px-12 py-4 flex items-center gap-2"
          >
            <span className="font-title uppercase">Get QR</span>
            <span className="text-white/60">|</span>
            <span className="font-arabic font-bold">احصل على الرمز</span>
            <span>→</span>
          </button>
        </footer>

        {/* Status */}
        <div className="flex items-center justify-center gap-2 text-[#6B7770] text-[10px] uppercase tracking-[0.2em]">
          <div className="w-2 h-2 bg-[#0e734b] rounded-sm" />
          <span>AI Transformation Complete</span>
          <div className="w-2 h-2 bg-[#0e734b] rounded-sm" />
        </div>
      </div>
    </div>
  );
};

// Sticker overlay component for preview
// Must match the calculations in imageUtils.js applyStickerOverlay exactly
// Frame dimensions: 4% side borders, 12% top bar (of width), 18% bottom bar (of width)
// For 2:3 image: framed width = 1.08W, framed height = 1.8W (where W = original width)
const StickerOverlay = ({ sticker }) => {
  const stickerImage = STICKER_IMAGES[sticker.id];
  const rotation = sticker.rotation || 0;

  // Frame dimensions as percentages of the framed image
  // These MUST match imageUtils.js applyImageBanners calculations exactly
  // sideBorder = originalWidth * 0.04, framedWidth = originalWidth * 1.08
  // So: frameLeft = 0.04 / 1.08 = 3.7% of framed width
  // topBarHeight = originalWidth * 0.12, framedHeight = originalHeight + 0.3*originalWidth
  // For 2:3 ratio: framedHeight = 1.5W + 0.3W = 1.8W
  // So: frameTop = 0.12W / 1.8W = 6.67% of framed height
  // frameBottom = 0.18W / 1.8W = 10% of framed height
  const frameLeft = '3.7%';
  const frameRight = '3.7%';
  const frameTop = '6.67%';
  const frameBottom = '10%';

  // Position styles based on sticker config - constrained to image area within frame
  const getPositionStyles = () => {
    // Size relative to the image area width (92.6% of framed width)
    const adjustedSize = sticker.size * 0.926;
    const size = `${adjustedSize * 100}%`;
    // Padding matches imageUtils.js: imageAreaWidth * 0.03
    // imageAreaWidth = 92.6% of framed width, so padding = 0.926 * 0.03 = 2.78%
    const padding = '2.78%';
    const rotateStr = rotation !== 0 ? ` rotate(${rotation}deg)` : '';

    switch (sticker.position) {
      case 'top-left':
        return { top: `calc(${frameTop} + ${padding})`, left: `calc(${frameLeft} + ${padding})`, width: size, transform: rotateStr || undefined };
      case 'top-right':
        return { top: `calc(${frameTop} + ${padding})`, right: `calc(${frameRight} + ${padding})`, width: size, transform: rotateStr || undefined };
      case 'top-center':
        return { top: `calc(${frameTop} + ${padding})`, left: '50%', transform: `translateX(-50%)${rotateStr}`, width: size };
      case 'bottom-left':
        return { bottom: `calc(${frameBottom} + ${padding})`, left: `calc(${frameLeft} + ${padding})`, width: size, transform: rotateStr || undefined };
      case 'bottom-right':
        return { bottom: `calc(${frameBottom} + ${padding})`, right: `calc(${frameRight} + ${padding})`, width: size, transform: rotateStr || undefined };
      case 'bottom-center':
        return { bottom: `calc(${frameBottom} + ${padding})`, left: '50%', transform: `translateX(-50%)${rotateStr}`, width: size };
      case 'left-center':
        return { top: '50%', left: `calc(${frameLeft} + ${padding})`, transform: `translateY(-50%)${rotateStr}`, width: size };
      case 'right-center':
        return { top: '50%', right: `calc(${frameRight} + ${padding})`, transform: `translateY(-50%)${rotateStr}`, width: size };
      default:
        return { top: `calc(${frameTop} + ${padding})`, left: `calc(${frameLeft} + ${padding})`, width: size, transform: rotateStr || undefined };
    }
  };

  return (
    <img
      src={stickerImage}
      alt={sticker.name}
      className="absolute pointer-events-none"
      style={getPositionStyles()}
    />
  );
};

export default ResultScreen;
